import React, { useState, useEffect } from 'react';
import Modal from '../../../../components/Modal';
import TextField from '../../../../components/TextField';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import SearchableSelect from '../../../../components/SearchableSelect';
import { FiEdit2, FiUser, FiPhone, FiMail, FiPlus, FiTrash2 } from 'react-icons/fi';
import { db } from '../../../../firebase/config';
import { collection, getDocs, getDoc, doc, addDoc, deleteDoc } from 'firebase/firestore';
import type { PSO } from '../../service/psoService';

type PSODetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    pso: PSO | null;
    onUpdate: (updatedData: Partial<PSO>) => Promise<void>;
    bankId?: string;
};

export default function PSODetailsModal({ isOpen, onClose, pso, onUpdate, bankId }: PSODetailsModalProps) {
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editedPSO, setEditedPSO] = useState<Partial<PSO>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [managedPSOs, setManagedPSOs] = useState<PSO[]>([]);
    const [loadingManagedPSOs, setLoadingManagedPSOs] = useState(false);
    const [allPSOs, setAllPSOs] = useState<PSO[]>([]);
    const [selectedPSOToAdd, setSelectedPSOToAdd] = useState<string>('');
    const [addingPSO, setAddingPSO] = useState(false);
    const [removingPSO, setRemovingPSO] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setIsEditing(false);
            setEditedPSO({});
            setErrors({});
        }
    }, [isOpen]);

    useEffect(() => {
        if (pso) {
            setEditedPSO({
                name: pso.name || '',
                contact: pso.contact || '',
                email: pso.email || '',
                designation: pso.designation || '',
            });
        }
    }, [pso]);

    // Fetch all PSOs and managed PSOs for Regional Head
    useEffect(() => {
        if (!isOpen || !pso || !bankId || !pso.id) {
            setManagedPSOs([]);
            setAllPSOs([]);
            return;
        }

        const isRegionalHead = pso.designation?.toLowerCase().includes('regional');
        if (!isRegionalHead) {
            setManagedPSOs([]);
            setAllPSOs([]);
            return;
        }

        const fetchData = async () => {
            setLoadingManagedPSOs(true);
            try {
                // Fetch all PSOs for the bank
                const allPSOsSnap = await getDocs(collection(db, 'banks', bankId, 'pos'));
                const allPSOsList = allPSOsSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                } as PSO));

                // Fetch managed PSO IDs from managedPsos subcollection
                const managedSnap = await getDocs(
                    collection(db, 'banks', bankId, 'pos', pso.id, 'managedPsos')
                );

                // Fetch full PSO details for each managed PSO ID
                const managedPSOsList: PSO[] = [];
                for (const managedDoc of managedSnap.docs) {
                    const psoId = managedDoc.data().psoId;
                    const managedDocId = managedDoc.id;
                    try {
                        const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', psoId));
                        if (psoDoc.exists()) {
                            const managedPSO = {
                                id: psoDoc.id,
                                ...psoDoc.data(),
                                managedDocId: managedDocId
                            } as PSO & { managedDocId?: string };
                            managedPSOsList.push(managedPSO);
                        }
                    } catch (error) {
                        console.error(`Error fetching PSO ${psoId}:`, error);
                    }
                }

                setManagedPSOs(managedPSOsList);
                setAllPSOs(allPSOsList);
            } catch (error) {
                console.error('Error fetching data:', error);
                setManagedPSOs([]);
                setAllPSOs([]);
            } finally {
                setLoadingManagedPSOs(false);
            }
        };

        fetchData();
    }, [isOpen, pso, bankId]);

    const handleInputChange = (field: string, value: string) => {
        setEditedPSO(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!editedPSO.name?.trim()) {
            newErrors.name = 'Name is required';
        }
        if (editedPSO.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedPSO.email)) {
            newErrors.email = 'Invalid email format';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm() || !pso) return;

        setLoading(true);
        try {
            await onUpdate(editedPSO);
            setIsEditing(false);
        } catch (err: any) {
            console.error('Error updating PSO:', err);
            setErrors({ submit: err?.message || 'Failed to update PSO' });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (pso) {
            setEditedPSO({
                name: pso.name || '',
                contact: pso.contact || '',
                email: pso.email || '',
                designation: pso.designation || '',
            });
        }
        setIsEditing(false);
        setErrors({});
    };

    const handleClose = () => {
        if (isEditing) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                handleCancel();
                onClose();
            }
        } else {
            onClose();
        }
    };

    const handleAddPSO = async () => {
        if (!selectedPSOToAdd || !pso || !bankId || !pso.id) return;

        setAddingPSO(true);
        try {
            // Check if PSO is already managed
            const isAlreadyManaged = managedPSOs.some(mp => mp.id === selectedPSOToAdd);
            if (isAlreadyManaged) {
                setErrors({ submit: 'This PSO is already managed' });
                setAddingPSO(false);
                return;
            }

            // Add to managedPsos subcollection
            await addDoc(
                collection(db, 'banks', bankId, 'pos', pso.id, 'managedPsos'),
                { psoId: selectedPSOToAdd }
            );

            // Refresh managed PSOs list
            const managedSnap = await getDocs(
                collection(db, 'banks', bankId, 'pos', pso.id, 'managedPsos')
            );

            const managedPSOsList: PSO[] = [];
            for (const managedDoc of managedSnap.docs) {
                const psoId = managedDoc.data().psoId;
                const managedDocId = managedDoc.id;
                try {
                    const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', psoId));
                    if (psoDoc.exists()) {
                        const managedPSO = {
                            id: psoDoc.id,
                            ...psoDoc.data(),
                            managedDocId: managedDocId
                        } as PSO & { managedDocId?: string };
                        managedPSOsList.push(managedPSO);
                    }
                } catch (error) {
                    console.error(`Error fetching PSO ${psoId}:`, error);
                }
            }

            setManagedPSOs(managedPSOsList);
            setSelectedPSOToAdd('');
            setErrors({});
        } catch (error: any) {
            console.error('Error adding PSO:', error);
            setErrors({ submit: error?.message || 'Failed to add PSO' });
        } finally {
            setAddingPSO(false);
        }
    };

    const handleRemovePSO = async (managedPSO: PSO & { managedDocId?: string }) => {
        if (!pso || !bankId || !pso.id || !managedPSO.managedDocId) return;

        if (!window.confirm(`Are you sure you want to remove ${managedPSO.name} from managed PSOs?`)) {
            return;
        }

        setRemovingPSO(managedPSO.id);
        try {
            // Remove from managedPsos subcollection
            await deleteDoc(
                doc(db, 'banks', bankId, 'pos', pso.id, 'managedPsos', managedPSO.managedDocId)
            );

            // Refresh managed PSOs list
            const managedSnap = await getDocs(
                collection(db, 'banks', bankId, 'pos', pso.id, 'managedPsos')
            );

            const managedPSOsList: PSO[] = [];
            for (const managedDoc of managedSnap.docs) {
                const psoId = managedDoc.data().psoId;
                const managedDocId = managedDoc.id;
                try {
                    const psoDoc = await getDoc(doc(db, 'banks', bankId, 'pos', psoId));
                    if (psoDoc.exists()) {
                        const managedPSO = {
                            id: psoDoc.id,
                            ...psoDoc.data(),
                            managedDocId: managedDocId
                        } as PSO & { managedDocId?: string };
                        managedPSOsList.push(managedPSO);
                    }
                } catch (error) {
                    console.error(`Error fetching PSO ${psoId}:`, error);
                }
            }

            setManagedPSOs(managedPSOsList);
        } catch (error: any) {
            console.error('Error removing PSO:', error);
            setErrors({ submit: error?.message || 'Failed to remove PSO' });
        } finally {
            setRemovingPSO(null);
        }
    };

    const isRegionalHead = pso?.designation?.toLowerCase().includes('regional');

    // Get available PSOs for selection (exclude current Regional Head and already managed PSOs)
    const availablePSOs = allPSOs.filter(
        p => p.id !== pso?.id && !managedPSOs.some(mp => mp.id === p.id)
    );

    const psoSelectOptions = availablePSOs.map(p => ({
        id: p.id,
        label: p.name || '',
        value: p.id,
        details: {
            email: p.email,
            mobile: p.contact
        }
    }));

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={pso?.name || "PSO Details"}
            size={isRegionalHead ? "lg" : "md"}
        >
            <div className="p-6" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Edit Button - Only show for bank users */}
                {isBankUser && !isEditing && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={() => setIsEditing(true)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                            aria-label="Edit PSO details"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <TextField
                            label="Name *"
                            value={editedPSO.name ?? ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            readOnly={!isEditing}
                            prefix={<Icon icon={FiUser} className="text-gray-400" />}
                            className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
                        )}
                    </div>
                    <TextField
                        label="Contact"
                        type="tel"
                        value={editedPSO.contact ?? ''}
                        onChange={(e) => handleInputChange('contact', e.target.value)}
                        readOnly={!isEditing}
                        prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                    />
                    <div>
                        <TextField
                            label="Email"
                            type="email"
                            value={editedPSO.email ?? ''}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            readOnly={!isEditing}
                            prefix={<Icon icon={FiMail} className="text-gray-400" />}
                            className={errors.email ? 'border-red-500' : ''}
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                        )}
                    </div>
                    {editedPSO.designation && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Designation
                            </label>
                            <input
                                type="text"
                                value={editedPSO.designation}
                                readOnly
                                className="w-full rounded-xl border border-gray-300 bg-gray-100 py-2.5 px-4 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            />
                        </div>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}
                </div>

                {/* Managed PSOs Section for Regional Head */}
                {isRegionalHead && (
                    <div className="mt-6">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Managed PSOs
                            </h3>
                            {isBankUser && (
                                <div className="flex items-center gap-3">
                                    <div className="w-64">
                                        <SearchableSelect
                                            options={psoSelectOptions}
                                            value={selectedPSOToAdd}
                                            onChange={setSelectedPSOToAdd}
                                            placeholder="Select PSO to add..."
                                            disabled={addingPSO || loadingManagedPSOs}
                                        />
                                    </div>
                                    <Button
                                        onClick={handleAddPSO}
                                        disabled={!selectedPSOToAdd || addingPSO || loadingManagedPSOs}
                                        className="flex items-center gap-2"
                                    >
                                        <Icon icon={FiPlus} className="h-4 w-4" />
                                        {addingPSO ? 'Adding...' : 'Add PSO'}
                                    </Button>
                                </div>
                            )}
                        </div>
                        {errors.submit && (
                            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                                {errors.submit}
                            </div>
                        )}
                        {loadingManagedPSOs ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="text-sm text-gray-500 dark:text-gray-400">Loading managed PSOs...</div>
                            </div>
                        ) : managedPSOs.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                                                Name
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                                                Email
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                                                Contact
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                                                Designation
                                            </th>
                                            {isBankUser && (
                                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                                                    Actions
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                                        {managedPSOs.map((managedPSO) => (
                                            <tr key={managedPSO.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                                                    {managedPSO.name || '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                    {managedPSO.email || '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                    {managedPSO.contact || '-'}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-sm">
                                                    {managedPSO.designation ? (
                                                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                                            {managedPSO.designation}
                                                        </span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                {isBankUser && (
                                                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                                                        <button
                                                            onClick={() => handleRemovePSO(managedPSO as PSO & { managedDocId?: string })}
                                                            disabled={removingPSO === managedPSO.id}
                                                            className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-700 transition-all hover:bg-red-100 hover:border-red-400 disabled:opacity-50 dark:border-red-700 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                                                        >
                                                            <Icon icon={FiTrash2} className="h-4 w-4" />
                                                            {removingPSO === managedPSO.id ? 'Removing...' : 'Remove'}
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                No managed PSOs found
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                    {isEditing ? (
                        <>
                            <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <Button variant="secondary" onClick={handleClose}>
                            Close
                        </Button>
                    )}
                </div>
            </div>
        </Modal>
    );
}

